import React, { useEffect, useState } from "react";
import { Table, Modal, Button, OverlayTrigger, Tooltip, Form, Row, Col } from "react-bootstrap";
import { MdWarningAmber, MdFullscreenExit } from "react-icons/md";
import {
  FiFileText,
  FiChevronUp,
  FiChevronDown,
  FiInfo,
  FiList,
  FiGrid,
  FiFilter,
  FiSearch,
} from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";

// MAPPATURA MANUALE DELLE CAUSALI
const dizionarioCausali = {
  "MM": "Mancanza",
  "MC": "Mancanza Cassa",
  "EQ": "Eccedenza per Quantità",
  "ENA": "Eccedenza Non Attesa",
  "D": "Danneggiato",
  "DNA": "Danneggiato Non Atteso",
  "DNA DN_128017": "Danneggiato Non Atteso",
  "DANNEGGIATI": "Raggruppa tutte le causali di danneggiamento (D, DNA, DNA DN_...)",
};

const getGruppoCausale = (causale) => {
  if (!causale) return "ALTRO";
  const c = causale.trim().toUpperCase();
  if (c.startsWith("D")) return "DANNEGGIATI";
  return causale;
};

// 1. COLONNE PER LA VISTA LISTA COMPATTA (Card Standard - No Scroll Orizzontale)
const colonneListaCompatta = [
  { key: "ragione_sociale", label: "Fornitore", type: "text", width: "26%" },
  { key: "id_bem", label: "BEM", type: "text", width: "12%" },
  { key: "data_rimesso", label: "Data Rim.", type: "date", width: "13%" },
  { key: "numero_rimesso", label: "N° Rim.", type: "text", width: "13%" },
  { key: "precodice", label: "Precod.", type: "text", width: "10%" },
  { key: "articolo", label: "Articolo", type: "text", width: "14%" },
  { key: "causale", label: "Causale", type: "text", width: "12%" },
];

// 2. COLONNE PER LA VISTA LISTA COMPLETA (Fullscreen - 100% Larghezza)
const colonneListaCompleta = [
  { key: "ragione_sociale", label: "Fornitore", type: "text" },
  { key: "id_bem", label: "BEM", type: "text" },
  { key: "data_rimesso", label: "Data Rimesso", type: "date" },
  { key: "numero_rimesso", label: "Num. Rimesso", type: "text" },
  { key: "precodice", label: "Precodice", type: "text" },
  { key: "articolo", label: "Articolo", type: "text" },
  { key: "causale", label: "Causale", type: "text" },
  { key: "quantita", label: "Qtà", type: "num0" },
  { key: "quantita_sospesa", label: "Qtà Sosp.", type: "num0" },
  { key: "vu_lordo", label: "VU Lordo", type: "num" },
  { key: "sconti_1", label: "Sc.1", type: "num" },
  { key: "sconti_2", label: "Sc.2", type: "num" },
  { key: "sconti_3", label: "Sc.3", type: "num" },
  { key: "codice_collo", label: "Cod. Collo", type: "text" },
  { key: "nota", label: "Nota", type: "text" },
  { key: "nota_risoluzione", label: "Nota Risoluzione", type: "text" },
  { key: "data_risoluzione", label: "Data Ris.", type: "date" },
  { key: "risolto", label: "Risolto", type: "bool" },
  { key: "data_completamento_bem", label: "Compl. BEM", type: "date" },
  { key: "deposito", label: "Dep.", type: "text" },
  { key: "id_rimesso_reclamo", label: "ID Reclamo", type: "text" },
  { key: "codice_fornitore", label: "Cod. Forn.", type: "text" },
  { key: "data_inserimento", label: "Data Ins.", type: "date" },
  { key: "data_modifica", label: "Data Mod.", type: "date" },
];

const TabellaAnomalie = () => {
  const [loading, setLoading] = useState(true);
  const [totaleAlert, setTotaleAlert] = useState(0);

  const [riepilogoFornitori, setRiepilogoFornitori] = useState([]);
  const [elencoFornitoriUnici, setElencoFornitoriUnici] = useState([]);
  const [filtroFornitore, setFiltroFornitore] = useState("ALL"); // Filtro selezionato
  const [searchTerm, setSearchTerm] = useState(""); // Ricerca libera per fornitore/articolo/ecc.

  const [listaCausaliUniche, setListaCausaliUniche] = useState([]);
  const [allLM, setAllLM] = useState([]);
  const [allAnomalie, setAllAnomalie] = useState([]);

  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [modalitaVista, setModalitaVista] = useState("raggruppato");

  const [sortConfig, setSortConfig] = useState({
    key: "totaleAnomalie",
    direction: "desc",
  });

  // Ordinamento iniziale predefinito per data_rimesso
  const [sortConfigLista, setSortConfigLista] = useState({
    key: "data_rimesso",
    direction: "desc",
  });

  const [showAnomalieModal, setShowAnomalieModal] = useState(false);
  const [selectedAnomalieRows, setSelectedAnomalieRows] = useState([]);
  const [selectedFornitoreNome, setSelectedFornitoreNome] = useState("");
  const [causaleSelezionata, setCausaleSelezionata] = useState("");

  const [showLmModal, setShowLmModal] = useState(false);
  const [selectedLmRows, setSelectedLmRows] = useState([]);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const arrayAnomalie = (data?.ANOMALIE || []).flat();
        const arrayLM = (data?.LM || []).flat();

        setTotaleAlert(arrayAnomalie.length);
        setAllLM(arrayLM);
        setAllAnomalie(arrayAnomalie);

        const causaliSet = new Set();
        arrayAnomalie.forEach((a) => {
          if (a.causale) causaliSet.add(getGruppoCausale(a.causale));
        });
        const causaliUniche = Array.from(causaliSet).sort();
        setListaCausaliUniche(causaliUniche);

        const fornitoriSet = new Set([
          ...arrayAnomalie.map((a) => a.ragione_sociale || "FORNITORE NON SPECIFICATO"),
          ...arrayLM.map((lm) => lm["Ragione Sociale"] || "FORNITORE NON SPECIFICATO"),
        ]);

        const fornitoriOrdinati = Array.from(fornitoriSet).sort((a, b) => a.localeCompare(b));
        setElencoFornitoriUnici(fornitoriOrdinati);

        const righeFornitori = fornitoriOrdinati.map((fornitore) => {
          const anomalieFornitore = arrayAnomalie.filter(
            (a) => (a.ragione_sociale || "FORNITORE NON SPECIFICATO") === fornitore,
          );
          const lmFornitore = arrayLM.filter(
            (lm) => (lm["Ragione Sociale"] || "FORNITORE NON SPECIFICATO") === fornitore,
          );

          const conteggioCausali = {};
          causaliUniche.forEach((c) => {
            conteggioCausali[c] = 0;
          });
          anomalieFornitore.forEach((ant) => {
            const causaleGruppo = getGruppoCausale(ant.causale);
            if (conteggioCausali[causaleGruppo] !== undefined) {
              conteggioCausali[causaleGruppo]++;
            } else {
              conteggioCausali[causaleGruppo] = 1;
            }
          });

          const documentiLmDistinti = new Set(
            lmFornitore.map((lm) => lm["Num Doc"]).filter(Boolean)
          );

          return {
            nome: fornitore,
            totaleAnomalie: anomalieFornitore.length,
            dettaglioAnomalie: anomalieFornitore,
            conteggioCausali: conteggioCausali,
            totaleLM: documentiLmDistinti.size,
            dettaglioLM: lmFornitore,
          };
        });

        righeFornitori.sort((a, b) => {
          if (b.totaleAnomalie !== a.totaleAnomalie) {
            return b.totaleAnomalie - a.totaleAnomalie;
          }
          return b.totaleLM - a.totaleLM;
        });

        setRiepilogoFornitori(righeFornitori);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch:", err);
        setLoading(false);
      });
  }, []);

  const requestSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedFornitori = () => {
    let items = [...riepilogoFornitori];
    
    // Filtraggio selettore fornitore
    if (filtroFornitore !== "ALL") {
      items = items.filter((f) => f.nome === filtroFornitore);
    }

    // Ricerca libera: fornitore, articolo, causale, numero rimesso...
    if (searchTerm.trim() !== "") {
      const s = searchTerm.trim().toLowerCase();
      items = items.filter((f) => {
        if (f.nome.toLowerCase().includes(s)) return true;
        return f.dettaglioAnomalie.some(
          (a) =>
            (a.articolo || "").toLowerCase().includes(s) ||
            (a.causale || "").toLowerCase().includes(s) ||
            (a.numero_rimesso || "").toLowerCase().includes(s) ||
            (a.precodice || "").toLowerCase().includes(s) ||
            (a.codice_fornitore || "").toLowerCase().includes(s),
        );
      });
    }

    if (sortConfig !== null) {
      items.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        if (listaCausaliUniche.includes(sortConfig.key)) {
          valA = a.conteggioCausali[sortConfig.key] || 0;
          valB = b.conteggioCausali[sortConfig.key] || 0;
        }

        if (typeof valA === "string") {
          return sortConfig.direction === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }

        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      });
    }
    return items;
  };

  const requestSortLista = (key) => {
    let direction = "desc";
    if (sortConfigLista.key === key && sortConfigLista.direction === "desc") {
      direction = "asc";
    }
    setSortConfigLista({ key, direction });
  };

  const getSortedListaAnomalie = (colonne) => {
    let items = [...allAnomalie];

    // Applica filtro fornitore se attivo
    if (filtroFornitore !== "ALL") {
      items = items.filter(
        (a) => (a.ragione_sociale || "FORNITORE NON SPECIFICATO") === filtroFornitore
      );
    }

    // Ricerca libera: fornitore, articolo, causale, numero rimesso, precodice, nota...
    if (searchTerm.trim() !== "") {
      const s = searchTerm.trim().toLowerCase();
      items = items.filter(
        (a) =>
          (a.ragione_sociale || "").toLowerCase().includes(s) ||
          (a.articolo || "").toLowerCase().includes(s) ||
          (a.causale || "").toLowerCase().includes(s) ||
          (a.numero_rimesso || "").toLowerCase().includes(s) ||
          (a.precodice || "").toLowerCase().includes(s) ||
          (a.nota || "").toLowerCase().includes(s) ||
          (a.codice_fornitore || "").toLowerCase().includes(s) ||
          (a.id_bem || "").toLowerCase().includes(s),
      );
    }

    const colonna = colonne.find((c) => c.key === sortConfigLista.key);
    const tipo = colonna ? colonna.type : "text";

    items.sort((a, b) => {
      let valA = a[sortConfigLista.key];
      let valB = b[sortConfigLista.key];

      if (tipo === "num" || tipo === "num0") {
        valA = parseFloat(valA) || 0;
        valB = parseFloat(valB) || 0;
        return sortConfigLista.direction === "asc" ? valA - valB : valB - valA;
      }

      valA = valA ? String(valA) : "";
      valB = valB ? String(valB) : "";
      return sortConfigLista.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });

    return items;
  };

  const renderSortIconLista = (key) => {
    if (sortConfigLista.key !== key)
      return (
        <span className="ms-1 text-muted" style={{ opacity: 0.4 }}>
          <FiChevronDown size={10} />
        </span>
      );
    return sortConfigLista.direction === "asc" ? (
      <FiChevronUp className="ms-1 text-dark" size={12} />
    ) : (
      <FiChevronDown className="ms-1 text-dark" size={12} />
    );
  };

  const renderTooltipCausale = (causale) => (props) => (
    <Tooltip id={`causale-tooltip-${causale}`} {...props} style={{ ...props.style, fontSize: "0.78rem" }}>
      {dizionarioCausali[causale] || `Causale commerciale: ${causale}`}
    </Tooltip>
  );

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return (
        <span className="ms-1 text-muted" style={{ opacity: 0.4 }}>
          <FiChevronDown size={10} />
        </span>
      );
    return sortConfig.direction === "asc" ? (
      <FiChevronUp className="ms-1 text-dark" size={12} />
    ) : (
      <FiChevronDown className="ms-1 text-dark" size={12} />
    );
  };

  const formatData = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "-";
    const soloData = dateStr.split(" ")[0];
    return soloData.split("-").reverse().join("/");
  };

  const formatNumero = (valore, decimali = 2) => {
    if (valore === null || valore === undefined || valore === "") return "-";
    const num = parseFloat(valore);
    return isNaN(num) ? "-" : num.toFixed(decimali);
  };

  const formatValoreLista = (valore, tipo) => {
    if (valore === null || valore === undefined || valore === "") return "-";
    switch (tipo) {
      case "date":
        return formatData(valore);
      case "num":
        return formatNumero(valore, 2);
      case "num0":
        return formatNumero(valore, 0);
      case "bool":
        return valore === "1" || valore === 1 ? "Sì" : "No";
      default:
        return valore;
    }
  };

  const handleCausaleClick = (e, fornitore, causaleGruppo) => {
    e.stopPropagation();
    const righeFiltrate = fornitore.dettaglioAnomalie.filter(
      (a) => getGruppoCausale(a.causale) === causaleGruppo,
    );

    if (righeFiltrate.length === 0) return;

    setSelectedFornitoreNome(fornitore.nome);
    setCausaleSelezionata(causaleGruppo);
    setSelectedAnomalieRows(righeFiltrate);
    setShowAnomalieModal(true);
  };

  const handleFornitoreClick = (fornitore) => {
    if (fornitore.totaleAnomalie === 0) return;
    setSelectedFornitoreNome(fornitore.nome);
    setCausaleSelezionata("TUTTE");
    setSelectedAnomalieRows(fornitore.dettaglioAnomalie);
    setShowAnomalieModal(true);
  };

  const apriModaleLM = (e, fornitore) => {
    e.stopPropagation();
    setSelectedFornitoreNome(fornitore.nome);
    setSelectedLmRows(fornitore.dettaglioLM);
    setShowLmModal(true);
  };

  // MENU A TENDINA PER IL FILTRO RAGIONE SOCIALE
  const renderSelezionatoreFornitori = () => (
    <Form.Select
      size="sm"
      className="select-fornitore-custom"
      value={filtroFornitore}
      onChange={(e) => setFiltroFornitore(e.target.value)}
      title="Filtra per Fornitore"
    >
      <option value="ALL">Tutti i Fornitori ({elencoFornitoriUnici.length})</option>
      {elencoFornitoriUnici.map((f, i) => (
        <option key={i} value={f}>
          {f}
        </option>
      ))}
    </Form.Select>
  );

  // CAMPO DI RICERCA LIBERA (fornitore, articolo, causale, numero rimesso, ecc.)
  const renderCampoRicerca = () => (
    <div className="position-relative search-anomalie-wrapper">
      <FiSearch
        size={12}
        className="position-absolute search-anomalie-icon"
        style={{
          left: "8px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#94a3b8",
          pointerEvents: "none",
        }}
      />
      <Form.Control
        size="sm"
        className="search-anomalie-input"
        placeholder="Cerca fornitore, articolo, causale..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
  );

  const renderTabellaContenuto = () => (
    <Table hover className="table-clean border-0">
      <thead>
        <tr>
          <th style={{ width: "250px" }} onClick={() => requestSort("nome")}>
            Fornitore {renderSortIcon("nome")}
          </th>

          {listaCausaliUniche.map((causale, cIdx) => (
            <OverlayTrigger
              key={cIdx}
              placement="top"
              delay={{ show: 200, hide: 150 }}
              overlay={renderTooltipCausale(causale)}
            >
              <th
                className="text-center th-causale-interattiva"
                style={{ width: "120px" }}
                onClick={() => requestSort(causale)}
              >
                {causale === "DANNEGGIATI" ? "DANNEGGIATI" : causale} {renderSortIcon(causale)}
              </th>
            </OverlayTrigger>
          ))}

          <th
            style={{ width: "130px" }}
            className="text-center"
            onClick={() => requestSort("totaleLM")}
          >
            LM Aperte {renderSortIcon("totaleLM")}
          </th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={listaCausaliUniche.length + 2} className="text-center py-5 text-muted small">
              Aggregazione dati in corso...
            </td>
          </tr>
        ) : getSortedFornitori().length === 0 ? (
          <tr>
            <td colSpan={listaCausaliUniche.length + 2} className="text-center py-5 text-muted small">
              Nessun dato registrato per il fornitore selezionato.
            </td>
          </tr>
        ) : (
          getSortedFornitori().map((fornitore, idx) => (
            <tr
              key={idx}
              className="row-clean"
              onClick={() => handleFornitoreClick(fornitore)}
            >
              <td
                className="fw-semibold text-dark text-truncate"
                style={{ maxWidth: "250px" }}
                title={fornitore.nome}
              >
                {fornitore.nome}
              </td>

              {listaCausaliUniche.map((causale, cIdx) => {
                const qtaAnomalie = fornitore.conteggioCausali[causale] || 0;
                const haAnomalie = qtaAnomalie > 0;

                return (
                  <td
                    key={cIdx}
                    className="text-center"
                    onClick={(e) => {
                      if (haAnomalie) {
                        handleCausaleClick(e, fornitore, causale);
                      } else {
                        e.stopPropagation();
                      }
                    }}
                    style={{ cursor: haAnomalie ? "pointer" : "default" }}
                  >
                    {haAnomalie ? (
                      <span className="badge-conteggio-attivo text-danger fw-bold">
                        {qtaAnomalie}
                      </span>
                    ) : (
                      <span className="badge-conteggio-vuoto text-muted">
                        0
                      </span>
                    )}
                  </td>
                );
              })}

              <td className="text-center">
                {fornitore.totaleLM > 0 ? (
                  <span
                    className="link-lm d-inline-flex align-items-center gap-1"
                    onClick={(e) => apriModaleLM(e, fornitore)}
                  >
                    <FiFileText size={12} /> {fornitore.totaleLM} {fornitore.totaleLM === 1 ? 'DOC' : 'DOCS'}
                  </span>
                ) : (
                  <span className="text-muted opacity-50">-</span>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );

  const renderTabellaListaCompleta = (isFullscreen = false) => {
    const colonneAttive = isFullscreen ? colonneListaCompleta : colonneListaCompatta;
    const anomalieFiltrate = getSortedListaAnomalie(colonneAttive);

    return (
      <Table 
        hover 
        className={`table-clean border-0 w-100 ${isFullscreen ? 'table-fullscreen-lista' : 'table-compact-lista'}`}
        style={{ tableLayout: "fixed" }}
      >
        <thead>
          <tr>
            {colonneAttive.map((col) => (
              <th 
                key={col.key} 
                onClick={() => requestSortLista(col.key)} 
                style={{ width: col.width || "auto" }}
                className="text-truncate"
                title={col.label}
              >
                {col.label} {renderSortIconLista(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colonneAttive.length} className="text-center py-5 text-muted small">
                Caricamento anomalie...
              </td>
            </tr>
          ) : anomalieFiltrate.length === 0 ? (
            <tr>
              <td colSpan={colonneAttive.length} className="text-center py-5 text-muted small">
                Nessuna anomalia trovata.
              </td>
            </tr>
          ) : (
            anomalieFiltrate.map((item, idx) => (
              <tr key={idx}>
                {colonneAttive.map((col) => {
                  const valFormatted = formatValoreLista(item[col.key], col.type);

                  // CAUSALE: tooltip con il nome esteso al passaggio del mouse (dettaglio articolo per articolo)
                  if (col.key === "causale" && item[col.key]) {
                    return (
                      <td
                        key={col.key}
                        className="text-truncate fw-semibold text-danger"
                        title={String(item[col.key] || "")}
                      >
                        <OverlayTrigger
                          placement="top"
                          delay={{ show: 200, hide: 150 }}
                          overlay={renderTooltipCausale(item[col.key])}
                        >
                          <span>{valFormatted}</span>
                        </OverlayTrigger>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={col.key}
                      className={`text-truncate ${col.key === "causale" ? "fw-semibold text-danger" : ""}`}
                      title={String(item[col.key] || "")}
                    >
                      {valFormatted}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    );
  };

  const renderContenutoAttivo = (isFullscreen = false) =>
    modalitaVista === "lista" ? renderTabellaListaCompleta(isFullscreen) : renderTabellaContenuto();

  const pulsanteToggleVista = (dimensione = "sm") => (
    <Button
      variant={modalitaVista === "lista" ? "dark" : "outline-secondary"}
      size={dimensione}
      className="d-flex align-items-center gap-1 border-0"
      style={{ fontSize: "0.68rem", fontWeight: "700", padding: "4px 10px" }}
      onClick={(e) => {
        e.stopPropagation();
        setModalitaVista(modalitaVista === "lista" ? "raggruppato" : "lista");
      }}
      title={modalitaVista === "lista" ? "Torna alla vista raggruppata per fornitore" : "Mostra tutte le anomalie in elenco"}
    >
      {modalitaVista === "lista" ? (
        <>
          <FiGrid size={12} /> Raggruppato
        </>
      ) : (
        <>
          <FiList size={12} /> Lista
        </>
      )}
    </Button>
  );

  return (
    <>
      {/* CARD STANDARD WIDGET */}
      <div
        className="card"
        style={{
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          height: "380px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        {/* Header Widget */}
        <div className="p-3 bg-white border-bottom d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
            <span
              onClick={() => setShowFullscreenModal(true)}
              style={{
                fontWeight: "800",
                fontSize: "0.72rem",
                color: "#0f172a",
                letterSpacing: "0.5px",
                cursor: "pointer",
                whiteSpace: "nowrap"
              }}
              className="d-inline-flex align-items-center gap-1 titolo-header"
              title="Espandi a tutto schermo"
            >
              ANOMALIE BEM DEP. 04
            </span>
            <button
              type="button"
              className="anomalie-info-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowInfoModal(true);
              }}
              aria-label="Informazioni sul widget Anomalie BEM"
            >
              <FiInfo size={14} />
            </button>

            {/* SELEZIONATORE RAGIONE SOCIALE VICINO IL TITOLO */}
            <div style={{ maxWidth: "200px" }}>
              {renderSelezionatoreFornitori()}
            </div>

            {/* CAMPO DI RICERCA VICINO AL SELETTORE FORNITORE */}
            <div style={{ maxWidth: "170px" }}>
              {renderCampoRicerca()}
            </div>

            {pulsanteToggleVista("sm")}
          </div>

          <span
            className="text-danger fw-bold d-flex align-items-center gap-1 flex-shrink-0"
            style={{ fontSize: "0.68rem" }}
          >
            <MdWarningAmber size={14} /> {totaleAlert} ALERT
          </span>
        </div>

        {/* CSS personalizzato per la select e le tabelle */}
        <style>{`
          .titolo-header:hover { color: #0284c7 !important; }

          .anomalie-info-btn {
            width: 27px;
            height: 27px;
            border: 1px solid #dfe3e8;
            border-radius: 50%;
            background: #fff;
            color: #0284c7;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            transition: all 0.15s ease;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
            cursor: pointer;
            flex-shrink: 0;
          }
          .anomalie-info-btn:hover {
            background: #f0f9ff;
            border-color: #bae6fd;
            transform: translateY(-1px);
          }
          .anomalie-info-backdrop { z-index: 20990 !important; }
          .anomalie-info-card {
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            background: #fff;
            padding: 16px;
            height: 100%;
          }
          .anomalie-info-step {
            width: 28px;
            height: 28px;
            min-width: 28px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.72rem;
            font-weight: 800;
            background: #e0f2fe;
            color: #0284c7;
          }
          .anomalie-info-list {
            margin: 0;
            padding-left: 18px;
            color: #475569;
            font-size: 0.82rem;
          }
          .anomalie-info-list li + li { margin-top: 7px; }
          .anomalie-causale-code {
            display: inline-flex;
            min-width: 48px;
            justify-content: center;
            align-items: center;
            padding: 3px 7px;
            border-radius: 6px;
            background: #fef2f2;
            border: 1px solid #fee2e2;
            color: #dc2626;
            font-weight: 800;
            font-size: 0.72rem;
          }

          .scroll-area { overflow: auto; flex-grow: 1; background: #fff; }
          .table-clean { margin-bottom: 0; width: 100%; border-collapse: separate; border-spacing: 0; }
          
          .select-fornitore-custom {
            font-size: 0.68rem !important;
            font-weight: 600 !important;
            padding: 2px 22px 2px 8px !important;
            border-radius: 6px !important;
            border: 1px solid #cbd5e1 !important;
            color: #334155 !important;
            background-color: #f8fafc !important;
            cursor: pointer;
            text-overflow: ellipsis;
          }
          .select-fornitore-custom:focus {
            border-color: #0284c7 !important;
            box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15) !important;
          }

          .search-anomalie-wrapper { display: flex; align-items: center; }
          .search-anomalie-input {
            font-size: 0.68rem !important;
            font-weight: 500 !important;
            padding: 3px 8px 3px 22px !important;
            border-radius: 6px !important;
            border: 1px solid #cbd5e1 !important;
            color: #334155 !important;
            background-color: #f8fafc !important;
          }
          .search-anomalie-input:focus {
            border-color: #0284c7 !important;
            box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15) !important;
          }
          .search-anomalie-input::placeholder { color: #94a3b8; }

          .table-clean thead th { 
            position: sticky; top: 0; z-index: 20; 
            background-color: #fff !important; color: #64748b !important; 
            font-size: 0.65rem; text-transform: uppercase; padding: 12px 6px;
            border-bottom: 1px solid #e2e8f0 !important;
            font-weight: 700; letter-spacing: 0.5px;
            cursor: pointer;
            user-select: none;
          }
          .table-clean thead th:hover {
            color: #0f172a !important;
            background-color: #f8fafc !important;
          }
          
          .table-clean tbody td { 
            font-size: 0.75rem; padding: 10px 6px; 
            vertical-align: middle; border-bottom: 1px solid #f1f5f9;
            white-space: nowrap;
          }
          
          .table-compact-lista thead th { padding: 10px 4px !important; font-size: 0.62rem !important; }
          .table-compact-lista tbody td { padding: 8px 4px !important; font-size: 0.70rem !important; }

          .table-fullscreen-lista thead th { padding: 10px 3px !important; font-size: 0.58rem !important; }
          .table-fullscreen-lista tbody td { padding: 8px 3px !important; font-size: 0.68rem !important; }

          .row-clean { transition: background 0.12s ease; cursor: pointer; }
          .row-clean:hover { background-color: #f8fafc !important; }
          
          .badge-conteggio-attivo {
            background-color: #fef2f2;
            color: #ef4444;
            padding: 4px 8px;
            border-radius: 6px;
            border: 1px solid #fee2e2;
            display: inline-block;
            min-width: 28px;
            transition: all 0.15s;
          }
          .badge-conteggio-attivo:hover {
            background-color: #ef4444;
            color: #fff;
          }

          .badge-conteggio-vuoto {
            background-color: #f8fafc;
            color: #94a3b8;
            padding: 4px 8px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
            display: inline-block;
            min-width: 28px;
            font-weight: 500;
            user-select: none;
          }
          
          .link-lm { 
            color: #0284c7; 
            font-weight: 700; 
            cursor: pointer; 
            padding: 4px 8px; 
            border-radius: 6px; 
            background-color: #f0f9ff;
            border: 1px solid #e0f2fe;
            transition: all 0.15s; 
          }
          .link-lm:hover { 
            color: #fff; 
            background-color: #0284c7; 
          }
        `}</style>

        {/* Corpo della Tabella nella Card */}
        <div className="scroll-area p-0">{renderContenutoAttivo(false)}</div>
      </div>

      {/* ==========================================================
          MODALE INFORMAZIONI WIDGET
      ========================================================== */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        scrollable
        className="p-5"
        style={{ zIndex: 21000 }}
        backdropClassName="anomalie-info-backdrop"
      >
        <Modal.Header closeButton className="border-bottom bg-white px-4 py-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle text-danger flex-shrink-0"
              style={{ width: "40px", height: "40px" }}
            >
              <MdWarningAmber size={21} />
            </div>
            <div>
              <Modal.Title className="fw-bold" style={{ fontSize: "1.05rem" }}>
                Come usare Anomalie BEM — Deposito 04
              </Modal.Title>
              <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
                Guida alla lettura delle anomalie, delle causali e dei documenti LM associati ai fornitori.
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          {/* INTRODUZIONE */}
          <div className="bg-danger-subtle border border-danger-subtle rounded-4 p-3 mb-3">
            <div className="fw-bold text-danger mb-1">A cosa serve</div>
            <div className="text-dark" style={{ fontSize: "0.84rem", lineHeight: 1.55 }}>
              Il pannello concentra in un'unica vista le <strong>anomalie rilevate sulle BEM</strong>
              e i <strong>documenti LM</strong> collegati ai fornitori. La vista principale permette
              di capire rapidamente quali fornitori presentano anomalie, con quale causale e quante
              segnalazioni risultano presenti.
            </div>
          </div>

          <Row className="g-3 mb-3">
            {/* 1 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">1</span>
                  <div>
                    <div className="fw-bold text-dark">Dati utilizzati</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Anomalie BEM e documenti LM
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>
                    Le anomalie provengono dal gruppo dati <strong>ANOMALIE</strong>.
                  </li>
                  <li>
                    I documenti LM provengono dal gruppo dati <strong>LM</strong>.
                  </li>
                  <li>
                    Il totale <strong>ALERT</strong> indica il numero complessivo di righe anomale
                    presenti nel dataset.
                  </li>
                  <li>
                    Il widget è dedicato operativamente al <strong>Deposito 04</strong>, come
                    indicato nel titolo.
                  </li>
                </ul>
              </div>
            </Col>

            {/* 2 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">2</span>
                  <div>
                    <div className="fw-bold text-dark">Vista raggruppata</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Matrice anomalie per fornitore
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>
                    Ogni riga rappresenta un <strong>fornitore</strong>.
                  </li>
                  <li>
                    Ogni colonna causale mostra quante anomalie di quel tipo sono associate al
                    fornitore.
                  </li>
                  <li>
                    Un valore <strong>0</strong> significa che non risultano anomalie di quella
                    causale per il fornitore.
                  </li>
                  <li>
                    Cliccando sul numero di una causale si apre direttamente il dettaglio delle
                    sole righe corrispondenti.
                  </li>
                  <li>
                    Cliccando sul nome/riga del fornitore si apre invece il dettaglio di
                    <strong> tutte le sue anomalie</strong>.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* 3 - CAUSALI */}
          <div className="anomalie-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="anomalie-info-step">3</span>
              <div>
                <div className="fw-bold text-dark">Significato delle causali</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Codici utilizzati per classificare le anomalie
                </div>
              </div>
            </div>

            <Row className="g-2">
              <Col md={6}>
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className="anomalie-causale-code">MM</span>
                  <div style={{ fontSize: "0.8rem" }}>
                    <strong>Mancanza</strong> — quantità o merce attesa risultata mancante.
                  </div>
                </div>
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className="anomalie-causale-code">MC</span>
                  <div style={{ fontSize: "0.8rem" }}>
                    <strong>Mancanza Cassa</strong> — anomalia classificata come mancanza riferita
                    alla cassa/collo.
                  </div>
                </div>
                <div className="d-flex align-items-start gap-2">
                  <span className="anomalie-causale-code">EQ</span>
                  <div style={{ fontSize: "0.8rem" }}>
                    <strong>Eccedenza per Quantità</strong> — quantità rilevata superiore a quella
                    prevista.
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className="anomalie-causale-code">ENA</span>
                  <div style={{ fontSize: "0.8rem" }}>
                    <strong>Eccedenza Non Attesa</strong> — presenza di merce eccedente non prevista.
                  </div>
                </div>
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className="anomalie-causale-code">D</span>
                  <div style={{ fontSize: "0.8rem" }}>
                    <strong>Danneggiato</strong> — articolo rilevato danneggiato.
                  </div>
                </div>
                <div className="d-flex align-items-start gap-2">
                  <span className="anomalie-causale-code">DNA</span>
                  <div style={{ fontSize: "0.8rem" }}>
                    <strong>Danneggiato Non Atteso</strong> — danneggiamento non previsto.
                  </div>
                </div>
              </Col>
            </Row>

            <div
              className="mt-3 p-3 rounded-3 bg-light border"
              style={{ fontSize: "0.8rem", lineHeight: 1.5 }}
            >
              <strong>Raggruppamento DANNEGGIATI:</strong> nella vista raggruppata tutte le causali
              che iniziano con la lettera <strong>D</strong> vengono accorpate sotto la voce
              <strong> DANNEGGIATI</strong>. Questo include, ad esempio, D, DNA e codici estesi
              come DNA DN_.... Nella vista lista resta visibile la causale originale della singola riga.
            </div>
          </div>

          <Row className="g-3 mb-3">
            {/* 4 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">4</span>
                  <div>
                    <div className="fw-bold text-dark">LM Aperte</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Conteggio documenti distinti
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                  La colonna <strong>LM Aperte</strong> non conta semplicemente le righe LM:
                  il sistema considera il numero di <strong>documenti LM distinti</strong> tramite
                  il campo <strong>Num Doc</strong>.
                </div>
                <div className="mt-3 p-3 rounded-3 bg-light border" style={{ fontSize: "0.78rem" }}>
                  Se lo stesso documento LM contiene più articoli, viene contato una sola volta
                  nella colonna riepilogativa. Cliccando sul collegamento <strong>DOC / DOCS</strong>
                  si apre comunque il registro con tutte le righe LM associate al fornitore.
                </div>
              </div>
            </Col>

            {/* 5 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">5</span>
                  <div>
                    <div className="fw-bold text-dark">Filtri e ricerca</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Come restringere velocemente i risultati
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>
                    Il selettore <strong>Fornitore</strong> limita la vista a una specifica ragione
                    sociale.
                  </li>
                  <li>
                    La ricerca libera può trovare fornitore, articolo, causale, numero rimesso,
                    precodice e codice fornitore.
                  </li>
                  <li>
                    Nella vista lista la ricerca comprende anche <strong>nota</strong> e
                    <strong> ID BEM</strong>.
                  </li>
                  <li>
                    Filtro fornitore e ricerca possono essere utilizzati contemporaneamente.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            {/* 6 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">6</span>
                  <div>
                    <div className="fw-bold text-dark">Vista Lista</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Analisi riga per riga
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>
                    Il pulsante <strong>Lista</strong> passa dalla matrice aggregata all'elenco
                    delle singole anomalie.
                  </li>
                  <li>
                    Nella card compatta vengono mostrate solo le colonne principali:
                    fornitore, BEM, rimesso, precodice, articolo e causale.
                  </li>
                  <li>
                    Nel fullscreen la lista espone anche quantità, valore lordo, sconti,
                    collo, note, risoluzione, deposito, reclamo e date di inserimento/modifica.
                  </li>
                  <li>
                    Le intestazioni sono ordinabili cliccandole.
                  </li>
                </ul>
              </div>
            </Col>

            {/* 7 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">7</span>
                  <div>
                    <div className="fw-bold text-dark">Dettaglio anomalia</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Informazioni operative della segnalazione
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>
                    <strong>Causale</strong>: classificazione dell'anomalia.
                  </li>
                  <li>
                    <strong>Articolo</strong>: codice interessato.
                  </li>
                  <li>
                    <strong>Num. Rimesso / Data Rimesso</strong>: riferimento logistico
                    dell'operazione.
                  </li>
                  <li>
                    <strong>Compl. BEM</strong>: data di completamento BEM; se manca viene indicato
                    <strong> In attesa</strong>.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            {/* 8 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">8</span>
                  <div>
                    <div className="fw-bold text-dark">Registro documenti LM</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Dettaglio delle righe LM del fornitore
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>Deposito, tipo e numero documento.</li>
                  <li>Data documento, precodice, articolo e descrizione.</li>
                  <li>Quantità, netto e importo.</li>
                  <li>
                    Le note vengono lette da <strong>Note Lista</strong>; se assenti, viene usato
                    <strong> Note Doc</strong>.
                  </li>
                </ul>
              </div>
            </Col>

            {/* 9 */}
            <Col md={6}>
              <div className="anomalie-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="anomalie-info-step">9</span>
                  <div>
                    <div className="fw-bold text-dark">Ordinamento</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Individuare prima i casi più rilevanti
                    </div>
                  </div>
                </div>
                <ul className="anomalie-info-list">
                  <li>
                    La vista raggruppata parte ordinata per <strong>totale anomalie decrescente</strong>.
                  </li>
                  <li>
                    In caso di parità, il riepilogo iniziale privilegia il fornitore con più
                    documenti LM.
                  </li>
                  <li>
                    Puoi ordinare per fornitore, singola causale o numero di LM.
                  </li>
                  <li>
                    La vista lista parte ordinata per <strong>Data Rimesso decrescente</strong>.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          {/* 10 */}
          <div className="anomalie-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="anomalie-info-step">10</span>
              <div>
                <div className="fw-bold text-dark">Vista a schermo intero</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Analisi estesa senza perdere filtri e modalità
                </div>
              </div>
            </div>
            <div className="d-flex align-items-start gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
                style={{ width: "36px", height: "36px" }}
              >
                <FiGrid size={16} />
              </div>
              <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                Cliccando sul titolo <strong>ANOMALIE BEM DEP. 04</strong> si apre la vista
                fullscreen. Il filtro fornitore, la ricerca e la modalità
                <strong> Raggruppato / Lista</strong> restano gli stessi, consentendo di continuare
                l'analisi con più spazio a disposizione.
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
            Suggerimento: usa la vista raggruppata per individuare il problema e la vista Lista per
            analizzare la singola anomalia.
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={() => setShowInfoModal(false)}>
              Chiudi
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreenModal(true);
              }}
            >
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* MODALE TUTTO SCHERMO */}
      <Modal
        show={showFullscreenModal}
        onHide={() => setShowFullscreenModal(false)}
        fullscreen={true}
        className="modal-fullscreen-custom"
      >
        <Modal.Header className="px-4 py-3 bg-white border-bottom d-flex align-items-center justify-content-between">
          <Modal.Title
            className="d-flex align-items-center gap-2"
            style={{ fontSize: "1rem", fontWeight: "800", color: "#0f172a" }}
          >
            ANOMALIE BEM DEP. 04
            <button
              type="button"
              className="anomalie-info-btn ms-1"
              onClick={(e) => {
                e.stopPropagation();
                setShowInfoModal(true);
              }}
              aria-label="Informazioni sul widget Anomalie BEM"
            >
              <FiInfo size={15} />
            </button>
            <div style={{ maxWidth: "250px" }}>
              {renderSelezionatoreFornitori()}
            </div>
            <div style={{ maxWidth: "200px" }}>
              {renderCampoRicerca()}
            </div>
            <span
              className="badge bg-danger-subtle text-danger border border-danger-subtle ms-1"
              style={{ fontSize: "0.7rem", padding: "5px 10px" }}
            >
              {totaleAlert} ALERT TOTALI
            </span>
            <span className="ms-2">{pulsanteToggleVista("sm")}</span>
          </Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center gap-1 border-0"
            onClick={() => setShowFullscreenModal(false)}
            style={{ fontSize: "0.8rem", fontWeight: "600" }}
          >
            <MdFullscreenExit size={18} /> Chiudi Schermo Intero
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0 scroll-area">
          {renderContenutoAttivo(true)}
        </Modal.Body>
      </Modal>

      {/* MODALE 1: DETTAGLIO ANOMALIE FILTRATE */}
      <Modal show={showAnomalieModal} onHide={() => setShowAnomalieModal(false)} size="lg" centered className="p-5">
        <Modal.Header closeButton className="border-bottom-0 pt-3 pb-2 px-3">
          <Modal.Title style={{ fontSize: "0.85rem", fontWeight: "800", color: "#0f172a" }}>
            DETTAGLIO ANOMALIE — {selectedFornitoreNome} {causaleSelezionata !== "TUTTE" && `[CAUSALE: ${causaleSelezionata}]`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ maxHeight: "400px", overflowY: "auto" }}>
          <Table responsive hover className="align-middle m-0 table-clean">
            <thead>
              <tr>
                <th>Causale</th>
                <th>Articolo</th>
                <th>Num. Rimesso</th>
                <th>Data Rimesso</th>
                <th>Compl. BEM</th>
              </tr>
            </thead>
            <tbody>
              {selectedAnomalieRows.map((item, rIdx) => (
                <tr key={rIdx}>
                  <td className="fw-semibold text-danger">
                    {item.causale ? (
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 200, hide: 150 }}
                        overlay={renderTooltipCausale(item.causale)}
                      >
                        <span>{item.causale}</span>
                      </OverlayTrigger>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="fw-bold text-dark">{item.articolo || "-"}</td>
                  <td className="font-monospace text-secondary">
                    {item.numero_rimesso?.replace(/,$/, "") || "-"}
                  </td>
                  <td>{formatData(item.data_rimesso)}</td>
                  <td className={item.data_completamento_bem ? "text-success fw-medium" : "text-muted"}>
                    {item.data_completamento_bem ? formatData(item.data_completamento_bem) : "In attesa"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer className="border-top-0 p-2">
          <Button variant="secondary" size="sm" onClick={() => setShowAnomalieModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODALE 2: DETTAGLIO DOCUMENTI LM */}
      <Modal show={showLmModal} onHide={() => setShowLmModal(false)} size="xl" centered className="p-5">
        <Modal.Header closeButton className="border-bottom-0 pt-3 pb-2 px-3">
          <Modal.Title style={{ fontSize: "0.85rem", fontWeight: "800", color: "#0f172a" }}>
            REGISTRO DOCUMENTI LM — {selectedFornitoreNome}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ maxHeight: "450px", overflowY: "auto" }}>
          <Table responsive hover className="align-middle m-0 table-clean">
            <thead>
              <tr>
                <th>Dep</th>
                <th>Tipo Doc</th>
                <th>Num Doc</th>
                <th>Data Doc</th>
                <th>Precodice</th>
                <th>Articolo</th>
                <th>Descrizione</th>
                <th className="text-center">Qta</th>
                <th>Netto (€)</th>
                <th>Importo (€)</th>
                <th>Note Lista / Dettagli Reso</th>
              </tr>
            </thead>
            <tbody>
              {selectedLmRows.map((lm, index) => (
                <tr key={index}>
                  <td className="text-start">{lm["Deposito"] || "-"}</td>
                  <td className="fw-semibold text-info">{lm["Tipo Doc"] || "LM"}</td>
                  <td className="fw-semibold">{lm["Num Doc"] || "-"}</td>
                  <td>{formatData(lm["Data Doc"])}</td>
                  <td>{lm["Precodice"] || "-"}</td>
                  <td className="fw-bold text-primary">{lm["Articolo"] || "-"}</td>
                  <td className="text-truncate" style={{ maxWidth: "150px" }} title={lm["Descrizione"]}>
                    {lm["Descrizione"] || "-"}
                  </td>
                  <td className="fw-bold text-center">{formatNumero(lm["Qta"], 0)}</td>
                  <td>{formatNumero(lm["Netto"], 2)}</td>
                  <td className="fw-bold">{formatNumero(lm["Importo"], 2)}</td>
                  <td className="text-muted small" style={{ maxWidth: "220px", whiteSpace: "normal" }}>
                    {lm["Note Lista"] || lm["Note Doc"] || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer className="border-top-0 p-2">
          <Button variant="secondary" size="sm" onClick={() => setShowLmModal(false)}>
            Chiudi Registro
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TabellaAnomalie;